import React, { useState, useRef, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFocusTrap } from '../utils/accessibility';
import {
  Home,
  FileText,
  Building2,
  X,
  Workflow,
  Users,
  BarChart3,
  Coins,
  ScrollText,
  Wallet,
  HelpCircle,
  ClipboardList,
  Inbox,
} from 'lucide-react';
import { useInvoicePermission, useIsInvoiceAdmin } from '../hooks/useInvoicePermission';
import { BRAND } from '../branding/brandAssets';
import { AppShellTopBarAccount } from './AppShellTopBarAccount';
import { PlatformSwitcher } from './PlatformSwitcher';
import { shouldShowPlatformSwitcher } from '../utils/platformSwitch';

const Layout: React.FC = () => {
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  // const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const permissionRole = useInvoicePermission();
  const isAdmin = useIsInvoiceAdmin();
  const modalRef = useRef<HTMLDivElement>(null);

  // Trap focus in logout modal when open
  useFocusTrap(modalRef, showLogoutModal);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showLogoutModal) {
        setShowLogoutModal(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showLogoutModal]);

  const handleLogoutConfirm = () => {
    logout();
    setShowLogoutModal(false);
    navigate('/login');
  };

  // Check if user has permission
  const hasPermission = (permission: string): boolean => {
    if (permission === 'read_users') {
      return permissionRole === 'admin' || permissionRole === 'super_admin';
    }
    return true;
  };

  const navigation = [
    { name: 'Dashboard', href: '/ims/dashboard', icon: Home, adminOnly: true },
    { name: 'Invoices', href: '/ims/invoices', icon: FileText },
    { name: 'My Tray', href: '/ims/my-tray', icon: Inbox },
    { name: 'Financial Approvals', href: '/ims/financial-power-proposal', icon: ScrollText },
    { name: 'Vendors', href: '/ims/vendors', icon: Building2 },
    // { name: 'Vendor', href: '/vendor', icon: Store },

    { name: 'Department', href: '/ims/departments', icon: Workflow },
    { name: 'Currencies', href: '/ims/currencies', icon: Coins, adminOnly: true },
    { name: 'Debit Accounts', href: '/ims/debit-accounts', icon: Wallet, adminOnly: true },

    { name: 'User Management', href: '/ims/user-management', icon: Users, permission: 'read_users' },
    { name: 'Reports', href: '/ims/reports', icon: BarChart3 },
    { name: 'Approval Matrix', href: '/ims/approval-matrix', icon: ClipboardList },
    { name: "FAQ's", href: '/ims/faq', icon: HelpCircle },
  ];

  const filteredNavigation = navigation.filter(item => {
    // My Tray is only for operational roles (not admin / super_admin / auditor)
    if (item.name === 'My Tray') {
      if (
        permissionRole === 'admin' ||
        permissionRole === 'super_admin' ||
        permissionRole === 'auditor'
      ) {
        return false;
      }
    }

    // Super admin sees everything
    if (isAdmin) {
      return true;
    }

    // Hide super admin only items
    if ((item as any).superAdminOnly) {
      return false;
    }

    // Hide admin only items for non-admin users
    if ((item as any).adminOnly) {
      return false;
    }

    // Reports: same page for Initiator, Finance Team, Approver, Payment Processor, Auditor
    if (item.name === 'Reports') {
      return (
        permissionRole === 'invoice_initiator' ||
        permissionRole === 'finance_team' ||
        permissionRole === 'invoice_approver' ||
        permissionRole === 'payment_approver' ||
        permissionRole === 'auditor'
      );
    }

    // Non-admin roles
    if (permissionRole === 'invoice_initiator') {
      // Hide only User Management and Department
      return (
        item.name !== 'User Management' &&
        item.name !== 'Department' &&
        item.name !== 'Currencies' &&
        item.name !== 'Vendors'
      );
    } else if (permissionRole === 'finance_team') {
      // Hide User Management, Department, Vendors, Currencies, Financial Power Invoice
      return (
        item.name !== 'User Management' &&
        item.name !== 'Department' &&
        item.name !== 'Vendors' &&
        item.name !== 'Currencies' &&
        item.name !== 'Financial Power Proposal' &&
        item.name !== 'Financial Approvals'
      );
    } else if (permissionRole === 'payment_approver') {
      // Hide User Management, Department, Vendors, Currencies
      return (
        item.name !== 'User Management' &&
        item.name !== 'Department' &&
        item.name !== 'Vendors' &&
        item.name !== 'Currencies' &&
        item.name !== 'Financial Power Proposal' &&
        item.name !== 'Financial Approvals'
      );
    } else if (permissionRole === 'invoice_approver') {
      // Approver — same nav as before; Reports handled above
      return (
        item.name !== 'User Management' &&
        item.name !== 'Department' &&
        item.name !== 'Vendors' &&
        item.name !== 'Currencies'
      );
    } else if (permissionRole === 'auditor') {
      // Auditor — same Reports access and menu hides as Finance Team (My Tray already excluded above)
      return (
        item.name !== 'User Management' &&
        item.name !== 'Department' &&
        item.name !== 'Vendors' &&
        item.name !== 'Currencies' &&
        item.name !== 'Financial Power Proposal' &&
        item.name !== 'Financial Approvals'
      );
    } else {
      // Hide User Management, Department, Vendors, Currencies for other roles
      return (
        item.name !== 'User Management' &&
        item.name !== 'Department' &&
        item.name !== 'Vendors' &&
        item.name !== 'Currencies'
      );
    }
  });

  const imsNavLinkClass = (isActive: boolean) =>
    `app-shell-sidebar-link app-shell-nav-row group flex w-full min-w-0 items-stretch overflow-hidden rounded-lg text-sm font-medium no-underline ${
      isActive ? 'app-shell-nav-row--active' : 'app-shell-nav-row--inactive'
    }`;

  const imsNavIconClass = (isActive: boolean) =>
    `mr-3 h-5 w-5 shrink-0 ${isActive ? 'app-shell-nav-icon--active' : 'app-shell-nav-icon--inactive'}`;

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50">
      {/* Skip to main content link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
      >
        Skip to main content
      </a>

      {/* Sidebar (all breakpoints — no hamburger / mobile drawer) */}
      <div className="flex h-screen min-h-0 w-64 min-w-[16rem] max-w-[16rem] shrink-0">
        <div className="app-shell-sidebar flex h-full min-h-0 w-full flex-col overflow-hidden">
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="app-shell-sidebar-header shrink-0 px-3 pt-3 pb-1">
              <div className="app-shell-sidebar-header__brand-row">
                <Link
                  to="/ims"
                  className="app-shell-sidebar-header__logo-link rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                >
                  <img
                    src={BRAND.ims.logoLight}
                    alt="IMS — Invoice Management"
                    className="app-shell-sidebar-header__logo-img"
                    width={320}
                    height={72}
                    decoding="async"
                  />
                </Link>
                {shouldShowPlatformSwitcher(user) ? (
                  <PlatformSwitcher currentPlatform="ims" variant="ims-sidebar-inline" />
                ) : null}
              </div>
              <div className="app-shell-sidebar-header-divider mt-3 shrink-0" aria-hidden />
            </div>
            <nav
              className="sidebar-nav-scroll min-h-0 flex-1 basis-0 overflow-y-auto px-2 pb-2 pt-3 space-y-1"
              aria-label="Main navigation"
            >
              {filteredNavigation.map(item => {
                const Icon = item.icon;
                const isActive =
                  location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);

                // Check if user has permission to see this menu item
                if (item.permission && !hasPermission(item.permission)) {
                  return null;
                }

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={imsNavLinkClass(isActive)}
                    aria-current={isActive ? 'page' : undefined}
                    aria-label={`Navigate to ${item.name}${isActive ? ' (current page)' : ''}`}
                  >
                    <span className="app-shell-nav-row__accent" aria-hidden />
                    <span className="flex min-w-0 flex-1 items-center py-2.5 pl-2 pr-3">
                      <Icon className={imsNavIconClass(isActive)} aria-hidden="true" />
                      <span className="min-w-0 flex-1 truncate">{item.name}</span>
                    </span>
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top bar — shared shell chrome with account in header */}
        <div className="app-shell-top-bar relative z-10 flex min-h-[3.5rem] flex-shrink-0 items-stretch sm:min-h-[3.75rem]">
          <div className="flex min-w-0 flex-1 items-center justify-end px-4 py-2 sm:px-5 sm:py-2.5">
            <AppShellTopBarAccount user={user} onLogoutClick={() => setShowLogoutModal(true)} />
          </div>
        </div>

        {/* Page content */}
        <main
          id="main-content"
          className="flex-1 relative overflow-y-auto focus:outline-none overflow-x-hidden"
          role="main"
          tabIndex={-1}
        >
          <div className="py-6">
            <div className="w-full mx-auto px-4">
              <Outlet />
            </div>
          </div>
        </main>
      </div>

      {/* Logout Modal */}
      {showLogoutModal && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="logout-modal-title"
          aria-describedby="logout-modal-description"
        >
          <div ref={modalRef} className="bg-white rounded-md shadow-lg w-96 p-6" role="document">
            <div className="flex justify-between items-center mb-4">
              <h3 id="logout-modal-title" className="text-lg font-medium text-gray-900">
                Logout
              </h3>
              <button
                type="button"
                onClick={() => setShowLogoutModal(false)}
                className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded"
                aria-label="Close logout confirmation dialog"
              >
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <p id="logout-modal-description" className="text-sm text-gray-900 mb-6">
              Are you sure you want to logout from your account?
            </p>
            <div className="flex justify-center space-x-3">
              <button
                type="button"
                onClick={() => setShowLogoutModal(false)}
                className="btn-secondary"
                aria-label="Cancel logout"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLogoutConfirm}
                className="btn-primary"
                aria-label="Confirm logout"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
